import { useState } from 'react'
import { Flex } from '@radix-ui/themes'
import { DataTestIDs } from 'shared/constants'
import type { WalletAction } from '../hooks/use-wallet-actions'
import styles from '../neon-wallets.module.scss'

export interface WalletManageProps {
  /** An action runs for one of the wallets; no other may start meanwhile. */
  isBusy: boolean
  /** The action running for this wallet, if any. */
  pendingAction: WalletAction | null
  onMakePrimary: (address: string) => void
  onUnlink: (address: string) => void
}

interface Props extends WalletManageProps {
  address: string
  canMakePrimary: boolean
  canUnlink: boolean
}

const CONFIRM: Record<WalletAction, { text: string; button: string }> = {
  primary: {
    text: 'Make this wallet primary? Rewards are sent to the primary wallet, and other players see it as your address in Up/Down AI and 1vs1.',
    button: 'Make primary'
  },
  unlink: {
    text: 'Unlink this wallet? Bets placed from it stay on your account, and you can link it again later. Until then it cannot play.',
    button: 'Unlink'
  }
}

const RUNNING: Record<WalletAction, string> = {
  primary: 'Making primary…',
  unlink: 'Unlinking…'
}

/**
 * "Make primary" and "Unlink" of one wallet card (FE-305), each confirmed
 * inside the card: the primary wallet receives the rewards and is the
 * account's public address, and an unlinked wallet stops playing.
 */
export const WalletManageActions: React.FC<Props> = ({
  address,
  canMakePrimary,
  canUnlink,
  isBusy,
  pendingAction,
  onMakePrimary,
  onUnlink
}) => {
  const [confirming, setConfirming] = useState<WalletAction | null>(null)

  if (!canMakePrimary && !canUnlink) return null

  if (confirming) {
    const confirm = () => {
      setConfirming(null)
      if (confirming === 'primary') onMakePrimary(address)
      else onUnlink(address)
    }

    return (
      <Flex
        direction='column'
        gap='2'
        className={styles.notice}
        data-tone={confirming === 'primary' ? 'cyan' : undefined}
      >
        <span className={styles.noticeText}>{CONFIRM[confirming].text}</span>
        <Flex
          gap='2'
          className={styles.cardActions}
        >
          <button
            type='button'
            className={styles.secondaryButton}
            data-size='sm'
            onClick={() => setConfirming(null)}
            data-testid={DataTestIDs.buttonWalletActionCancel}
          >
            Cancel
          </button>
          <button
            type='button'
            className={styles.secondaryButton}
            data-size='sm'
            data-tone={confirming === 'unlink' ? 'danger' : undefined}
            disabled={isBusy}
            onClick={confirm}
            data-testid={DataTestIDs.buttonWalletActionConfirm}
          >
            {CONFIRM[confirming].button}
          </button>
        </Flex>
      </Flex>
    )
  }

  if (pendingAction) {
    return <span className={styles.noticeText}>{RUNNING[pendingAction]}</span>
  }

  return (
    <Flex
      gap='2'
      className={styles.cardActions}
    >
      {canMakePrimary ?
        <button
          type='button'
          className={styles.secondaryButton}
          data-size='sm'
          disabled={isBusy}
          onClick={() => setConfirming('primary')}
          data-testid={DataTestIDs.buttonWalletMakePrimary}
        >
          Make primary
        </button>
      : null}
      {canUnlink ?
        <button
          type='button'
          className={styles.secondaryButton}
          data-size='sm'
          data-tone='danger'
          disabled={isBusy}
          onClick={() => setConfirming('unlink')}
          data-testid={DataTestIDs.buttonWalletUnlink}
        >
          Unlink
        </button>
      : null}
    </Flex>
  )
}
